import React, { useState } from 'react';
import { XIcon, PlusIcon, SaveIcon, CheckIcon, AlertTriangleIcon, ShieldIcon, ThermometerIcon, ClipboardCheckIcon, DropletIcon, FlaskConicalIcon, ScaleIcon, PackageIcon, ClipboardIcon, ClipboardListIcon } from 'lucide-react';
interface WorkInstructionsProps {
  recipeId: string;
  recipeName: string;
  onClose: () => void;
}
interface Instruction {
  id: string;
  text: string;
  phase: 'before' | 'during' | 'after';
  department: 'APZ' | 'Mischabteilung' | 'Kochabteilung' | 'GKNass' | 'GKTrocken';
  isRelevant: boolean;
  icon: string;
}
export function WorkInstructions({
  recipeId,
  recipeName,
  onClose
}: WorkInstructionsProps) {
  // Beispiel-Anweisungen
  const [instructions, setInstructions] = useState<Instruction[]>([{
    id: '1',
    text: 'Arbeitsplatz reinigen und desinfizieren',
    phase: 'before',
    department: 'APZ',
    isRelevant: true,
    icon: 'spray'
  }, {
    id: '2',
    text: 'Persönliche Schutzausrüstung anlegen',
    phase: 'before',
    department: 'APZ',
    isRelevant: true,
    icon: 'shield'
  }, {
    id: '3',
    text: 'Rohstoffe auf Qualität und Menge prüfen',
    phase: 'before',
    department: 'APZ',
    isRelevant: true,
    icon: 'scale'
  }, {
    id: '4',
    text: 'Temperatur alle 30 Minuten kontrollieren',
    phase: 'during',
    department: 'Kochabteilung',
    isRelevant: true,
    icon: 'thermometer'
  }, {
    id: '5',
    text: 'Proben für Qualitätskontrolle entnehmen',
    phase: 'during',
    department: 'Mischabteilung',
    isRelevant: false,
    icon: 'flask'
  }, {
    id: '6',
    text: 'Arbeitsbereich reinigen',
    phase: 'after',
    department: 'GKNass',
    isRelevant: true,
    icon: 'droplet'
  }, {
    id: '7',
    text: 'Produktionsbericht ausfüllen',
    phase: 'after',
    department: 'GKTrocken',
    isRelevant: true,
    icon: 'clipboard'
  }]);
  const [newInstruction, setNewInstruction] = useState({
    text: '',
    phase: 'before' as 'before' | 'during' | 'after',
    department: 'APZ' as 'APZ' | 'Mischabteilung' | 'Kochabteilung' | 'GKNass' | 'GKTrocken',
    icon: 'alert-triangle'
  });
  const handleAddInstruction = () => {
    if (!newInstruction.text.trim()) return;
    setInstructions([...instructions, {
      id: Date.now().toString(),
      text: newInstruction.text,
      phase: newInstruction.phase,
      department: newInstruction.department,
      isRelevant: true,
      icon: newInstruction.icon
    }]);
    setNewInstruction({
      text: '',
      phase: 'before',
      department: 'APZ',
      icon: 'alert-triangle'
    });
  };
  const toggleRelevance = (id: string) => {
    setInstructions(instructions.map(instruction => instruction.id === id ? {
      ...instruction,
      isRelevant: !instruction.isRelevant
    } : instruction));
  };
  // Funktion zum Rendern des Icons basierend auf dem Icon-Namen
  const renderIcon = (iconName: string, size = 18) => {
    switch (iconName) {
      case 'alert-triangle':
        return <AlertTriangleIcon size={size} data-id="element-2714" />;
      case 'shield':
        return <ShieldIcon size={size} data-id="element-2715" />;
      case 'thermometer':
        return <ThermometerIcon size={size} data-id="element-2716" />;
      case 'clipboard-check':
        return <ClipboardCheckIcon size={size} data-id="element-2717" />;
      case 'spray':
        return <div size={size} data-id="element-2718" />;
      case 'droplet':
        return <DropletIcon size={size} data-id="element-2719" />;
      case 'flask':
        return <FlaskConicalIcon size={size} data-id="element-2720" />;
      case 'scale':
        return <ScaleIcon size={size} data-id="element-2721" />;
      case 'package':
        return <PackageIcon size={size} data-id="element-2722" />;
      case 'clipboard':
        return <ClipboardIcon size={size} data-id="element-2723" />;
      default:
        return <AlertTriangleIcon size={size} data-id="element-2724" />;
    }
  };
  // Funktion zur Bestimmung der Farbe basierend auf der Abteilung
  const getDepartmentColor = (department: string) => {
    switch (department) {
      case 'APZ':
        return 'bg-gray-50 text-gray-800';
      case 'Mischabteilung':
        return 'bg-indigo-50 text-indigo-800';
      case 'Kochabteilung':
        return 'bg-violet-50 text-violet-800';
      case 'GKNass':
        return 'bg-emerald-50 text-emerald-800';
      case 'GKTrocken':
        return 'bg-rose-50 text-rose-800';
      default:
        return 'bg-gray-50 text-gray-800';
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2725">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" data-id="element-2726">
        <div className="flex justify-between items-center mb-4" data-id="element-2727">
          <h3 className="text-lg font-bold text-gray-800" data-id="element-2728">
            Arbeitsanweisungen für {recipeName} ({recipeId})
          </h3>
          <button onClick={onClose} className="p-1" data-id="element-2729">
            <XIcon size={20} data-id="element-2730" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2731">
          <div data-id="element-2732">
            <h4 className="font-semibold text-gray-700 mb-3" data-id="element-2733">
              Relevante Anweisungen für dieses Rezept
            </h4>
            <div className="space-y-4" data-id="element-2734">
              {instructions.filter(i => i.isRelevant).map(instruction => <div key={instruction.id} className={`flex p-3 border rounded-md ${getDepartmentColor(instruction.department)}`} data-id="element-2735">
                    <div className="mr-3 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2736">
                      {renderIcon(instruction.icon)}
                    </div>
                    <div className="flex-1" data-id="element-2737">
                      <div className="flex items-start justify-between" data-id="element-2738">
                        <div data-id="element-2739">
                          <p className="text-sm font-medium" data-id="element-2740">
                            {instruction.text}
                          </p>
                          <div className="flex items-center gap-2 mt-1" data-id="element-2741">
                            <span className="text-xs text-gray-500 inline-block" data-id="element-2742">
                              {instruction.phase === 'before' ? 'Vor der Produktion' : instruction.phase === 'during' ? 'Während der Produktion' : 'Nach der Produktion'}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-id="element-2743">
                              {instruction.department}
                            </span>
                          </div>
                        </div>
                        <button onClick={() => toggleRelevance(instruction.id)} className="text-xs text-red-500 hover:text-red-700 flex-shrink-0" data-id="element-2744">
                          Entfernen
                        </button>
                      </div>
                    </div>
                  </div>)}
              {instructions.filter(i => i.isRelevant).length === 0 && <p className="text-sm text-gray-500 italic" data-id="element-2745">
                  Keine relevanten Anweisungen ausgewählt
                </p>}
            </div>
          </div>
          <div data-id="element-2746">
            <h4 className="font-semibold text-gray-700 mb-3" data-id="element-2747">
              Verfügbare Anweisungen
            </h4>
            <div className="space-y-4" data-id="element-2748">
              <div className="mb-4" data-id="element-2749">
                <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2750">
                  Neue Anweisung hinzufügen
                </h5>
                <div className="space-y-2" data-id="element-2751">
                  <input type="text" className="w-full p-2 border border-gray-300 rounded-md" placeholder="Neue Arbeitsanweisung eingeben..." value={newInstruction.text} onChange={e => setNewInstruction({
                  ...newInstruction,
                  text: e.target.value
                })} data-id="element-2752" />
                  <div className="grid grid-cols-2 gap-2" data-id="element-2753">
                    <select className="w-full p-2 border border-gray-300 rounded-md" value={newInstruction.phase} onChange={e => setNewInstruction({
                    ...newInstruction,
                    phase: e.target.value as 'before' | 'during' | 'after'
                  })} data-id="element-2754">
                      <option value="before" data-id="element-2755">Vor der Produktion</option>
                      <option value="during" data-id="element-2756">Während der Produktion</option>
                      <option value="after" data-id="element-2757">Nach der Produktion</option>
                    </select>
                    <select className="w-full p-2 border border-gray-300 rounded-md" value={newInstruction.department} onChange={e => setNewInstruction({
                    ...newInstruction,
                    department: e.target.value as 'APZ' | 'Mischabteilung' | 'Kochabteilung' | 'GKNass' | 'GKTrocken'
                  })} data-id="element-2758">
                      <option value="APZ" data-id="element-2759">APZ</option>
                      <option value="Mischabteilung" data-id="element-2760">Mischabteilung</option>
                      <option value="Kochabteilung" data-id="element-2761">Kochabteilung</option>
                      <option value="GKNass" data-id="element-2762">Gewürzküche Nass</option>
                      <option value="GKTrocken" data-id="element-2763">Gewürzküche Trocken</option>
                    </select>
                  </div>
                  <div className="flex gap-2" data-id="element-2764">
                    <select className="w-full p-2 border border-gray-300 rounded-md" value={newInstruction.icon} onChange={e => setNewInstruction({
                    ...newInstruction,
                    icon: e.target.value
                  })} data-id="element-2765">
                      <option value="alert-triangle" data-id="element-2766">Warnung</option>
                      <option value="shield" data-id="element-2767">Schutz</option>
                      <option value="thermometer" data-id="element-2768">Temperatur</option>
                      <option value="clipboard-check" data-id="element-2769">Kontrolle</option>
                      <option value="spray" data-id="element-2770">Reinigung</option>
                      <option value="droplet" data-id="element-2771">Flüssigkeit</option>
                      <option value="flask" data-id="element-2772">Labor</option>
                      <option value="scale" data-id="element-2773">Wiegen</option>
                      <option value="package" data-id="element-2774">Material</option>
                      <option value="clipboard" data-id="element-2775">Dokumentation</option>
                    </select>
                    <button onClick={handleAddInstruction} className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md flex-shrink-0" data-id="element-2776">
                      <PlusIcon size={18} data-id="element-2777" />
                    </button>
                  </div>
                  <div className="p-2 border border-gray-200 rounded-md" data-id="element-2778">
                    <div className="text-xs text-gray-500 mb-2" data-id="element-2779">Vorschau:</div>
                    <div className={`flex p-2 rounded-md ${getDepartmentColor(newInstruction.department)}`} data-id="element-2780">
                      <div className="mr-3 flex-shrink-0" data-id="element-2781">
                        {renderIcon(newInstruction.icon)}
                      </div>
                      <div data-id="element-2782">
                        <p className="text-sm font-medium" data-id="element-2783">
                          {newInstruction.text || 'Neue Anweisung'}
                        </p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-2784">
                          <span className="text-xs text-gray-500" data-id="element-2785">
                            {newInstruction.phase === 'before' ? 'Vor der Produktion' : newInstruction.phase === 'during' ? 'Während der Produktion' : 'Nach der Produktion'}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-id="element-2786">
                            {newInstruction.department}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3" data-id="element-2787">
                <h5 className="text-sm font-medium text-gray-700" data-id="element-2788">
                  Vor der Produktion
                </h5>
                {instructions.filter(i => i.phase === 'before' && !i.isRelevant).map(instruction => <div key={instruction.id} className={`flex p-2 rounded-md ${getDepartmentColor(instruction.department)}`} data-id="element-2789">
                      <div className="mr-3 flex-shrink-0" data-id="element-2790">
                        {renderIcon(instruction.icon)}
                      </div>
                      <div className="flex-1 flex items-center justify-between" data-id="element-2791">
                        <div data-id="element-2792">
                          <span className="text-sm" data-id="element-2793">{instruction.text}</span>
                          <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-id="element-2794">
                            {instruction.department}
                          </span>
                        </div>
                        <button onClick={() => toggleRelevance(instruction.id)} className="text-xs text-blue-500 hover:text-blue-700" data-id="element-2795">
                          Hinzufügen
                        </button>
                      </div>
                    </div>)}
              </div>
              <div className="space-y-3" data-id="element-2796">
                <h5 className="text-sm font-medium text-gray-700" data-id="element-2797">
                  Während der Produktion
                </h5>
                {instructions.filter(i => i.phase === 'during' && !i.isRelevant).map(instruction => <div key={instruction.id} className={`flex p-2 rounded-md ${getDepartmentColor(instruction.department)}`} data-id="element-2798">
                      <div className="mr-3 flex-shrink-0" data-id="element-2799">
                        {renderIcon(instruction.icon)}
                      </div>
                      <div className="flex-1 flex items-center justify-between" data-id="element-2800">
                        <div data-id="element-2801">
                          <span className="text-sm" data-id="element-2802">{instruction.text}</span>
                          <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-id="element-2803">
                            {instruction.department}
                          </span>
                        </div>
                        <button onClick={() => toggleRelevance(instruction.id)} className="text-xs text-blue-500 hover:text-blue-700" data-id="element-2804">
                          Hinzufügen
                        </button>
                      </div>
                    </div>)}
              </div>
              <div className="space-y-3" data-id="element-2805">
                <h5 className="text-sm font-medium text-gray-700" data-id="element-2806">
                  Nach der Produktion
                </h5>
                {instructions.filter(i => i.phase === 'after' && !i.isRelevant).map(instruction => <div key={instruction.id} className={`flex p-2 rounded-md ${getDepartmentColor(instruction.department)}`} data-id="element-2807">
                      <div className="mr-3 flex-shrink-0" data-id="element-2808">
                        {renderIcon(instruction.icon)}
                      </div>
                      <div className="flex-1 flex items-center justify-between" data-id="element-2809">
                        <div data-id="element-2810">
                          <span className="text-sm" data-id="element-2811">{instruction.text}</span>
                          <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700" data-id="element-2812">
                            {instruction.department}
                          </span>
                        </div>
                        <button onClick={() => toggleRelevance(instruction.id)} className="text-xs text-blue-500 hover:text-blue-700" data-id="element-2813">
                          Hinzufügen
                        </button>
                      </div>
                    </div>)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200" data-id="element-2814">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-2815">
            Schließen
          </button>
          <button className="px-4 py-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white rounded-lg flex items-center gap-1" data-id="element-2816">
            <SaveIcon size={16} data-id="element-2817" />
            <span data-id="element-2818">Speichern</span>
          </button>
        </div>
      </div>
    </div>;
}